import { useState, useCallback, useMemo, useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import SearchBar from '../../../components/feature/SearchBar';
import SegmentedControl, { type SegmentedOption } from '../../../components/base/SegmentedControl';
import DomainIcon from '../../../components/base/icons/domain/DomainIcon';
import PropertyTypePicker from '../../../components/search/PropertyTypePicker';
import RangeFilterGrid from '../../../components/search/RangeFilterGrid';
import SelectMenu, { type SelectMenuOption } from '../../../components/base/SelectMenu';
import {
  getSearchAmenities,
  getSearchFilterFields,
  rangesFromFilters,
  type SearchAmenityKey,
  type SearchRangeBounds,
  type SearchRangeKey,
} from '../../../config/searchFilterFields';
import type { SearchTypeOption } from '../../../config/propertyTypeCatalog';
import { formatAmount } from '../../../utils/numberFormat';
import LandmarkSearch from '../../../components/feature/LandmarkSearch';
import RadiusSelector from '../../../components/feature/RadiusSelector';
import { areFiltersEqual, defaultFilters } from '../urlState';
import { categoryChange, listingTypeChange, pricePeriodChange, rangeChange, typeChange } from '../filterActions';
import type { FilterState, LandmarkFilter } from '../types';
import type { PlaceResult } from '../../../hooks/usePlacesAutocomplete';

/**
 * The /search filters panel: the desktop sidebar and, with `layout="sheet"`, the
 * body of the mobile filter sheet (page.tsx mounts both). See Apps/web/THEME.md.
 *
 * - «نوع العرض» and «الفئة» are SegmentedControls; «نوع العقار» is the inline
 *   PropertyTypePicker; bedrooms, bathrooms, price and area are ONE RangeFilterGrid
 *   (the same composites as the home hero, so both surfaces behave alike).
 * - Which ranges and amenity toggles show follows the chosen type / category /
 *   listing type (config/searchFilterFields): land has no bedrooms and no pool,
 *   تجاري with no type shows only price and area, pet-friendly is rent-only.
 *   page.tsx prunes the values these hide, so they are never sent.
 * - Category and type sit ABOVE the ranges they decide, so choosing one never
 *   shifts the control under the pointer.
 */

interface FiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: Partial<FilterState>) => void;
  onClearAll: () => void;
  /** Total matching listings, shown beside the title. */
  resultCount: number;
  /** Prefix for field ids. Must be unique on the page, which mounts the panel twice. */
  idPrefix?: string;
  /**
   * 'sidebar' (default): a card with its own title and «مسح الكل».
   * 'sheet': bare content for the mobile sheet, which has its own title and clear bar.
   */
  layout?: 'sidebar' | 'sheet';
  /**
   * The results order («الأحدث أولاً»، «الأقل سعراً»…). Shown first in the panel, with
   * the filters, instead of in the results toolbar (owner, 2026-10).
   */
  sort?: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: readonly SelectMenuOption[];
  };
}

type ListingTypeValue = FilterState['listingType'];
type CategoryValue = FilterState['category'];

/** Domain-icon registry id for each amenity toggle. */
const AMENITY_ICON: Record<SearchAmenityKey, string> = {
  parking: 'parking',
  petFriendly: 'pet_friendly',
  furnished: 'furnished',
  swimmingPool: 'pool',
  gym: 'gym',
  garden: 'garden',
  elevator: 'elevator',
  security: 'security',
};

// Proportion rule (THEME): 0.9375rem titles with 1.125rem icons; 0.875rem labels
// with 1rem icons in a 2rem tile; touch targets at least 2.75rem.
const TITLE = 'flex items-center gap-2 text-[0.9375rem] leading-snug text-[var(--pv-color-text)]';
const TITLE_ICON = 'text-[1.125rem] leading-none text-[var(--pv-color-primary)]';
const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40]/20 dark:focus-visible:ring-[#3FBBA2]/25';
// Bordered controls also turn their border teal on focus (as OptionTile does), so the
// focus state does not rest on the faint ring alone.
const FOCUS_BORDER = 'focus-visible:border-[var(--pv-color-primary)]';

/** One block of the panel; blocks are split by a hairline, never boxed (no cards in the card). */
function FilterSection({
  title,
  titleId,
  icon,
  action,
  children,
}: {
  title?: ReactNode;
  /** Lets a control in the section name itself after the heading (aria-labelledby). */
  titleId?: string;
  icon?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3 border-t border-[var(--pv-border-subtle)] pt-5 first:border-t-0 first:pt-0">
      {(title || action) && (
        <div className="flex min-h-[1.75rem] items-center justify-between gap-3">
          {title ? (
            <h4 id={titleId} className={TITLE}>
              {icon && <i className={`${icon} ${TITLE_ICON}`} aria-hidden="true" />}
              <span>{title}</span>
            </h4>
          ) : (
            <span />
          )}
          {action}
        </div>
      )}
      {children}
    </section>
  );
}

export default function PropertyFilters({
  filters,
  onFiltersChange,
  onClearAll,
  resultCount,
  idPrefix,
  layout = 'sidebar',
  sort,
}: FiltersProps) {
  const { t, i18n } = useTranslation(['search', 'common', 'property']);
  const isArabic = i18n.language === 'ar';
  const [showAmenities, setShowAmenities] = useState(true);

  const autoId = useId();
  const baseId = idPrefix ?? `search-filters-${autoId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const typePickerId = `${baseId}-type`;
  const amenitiesListId = `${baseId}-amenities`;

  // Per-type field rules (config/searchFilterFields) — the same logic the home
  // hero uses. Labels: hotels say «غرف», land says «مساحة الأرض».
  const searchFields = useMemo(
    () => getSearchFilterFields(filters.propertyType, filters.category),
    [filters.propertyType, filters.category],
  );
  const bedroomsLabel = isArabic ? searchFields.bedroomsLabelAr : searchFields.bedroomsLabelEn;
  const areaLabel = isArabic ? searchFields.areaLabelAr : searchFields.areaLabelEn;
  const bathroomsLabel = t('search.filters.bathroomsLabel');
  const areaUnit = t('ui:range.unitSqm');
  const currencyLabel = t('common:currency.short');

  // The eight flat range keys as the grid's { min, max } pairs (cheap; no memo needed).
  const ranges = rangesFromFilters(filters);

  // The toggles that make sense for this type / category / listing type.
  const amenityKeys = useMemo(
    () => getSearchAmenities(filters.propertyType, filters.category, filters.listingType),
    [filters.propertyType, filters.category, filters.listingType],
  );

  // Something to reset? The defaults (للإيجار + سكني) are not "filters".
  const hasActiveFilters = useMemo(() => !areFiltersEqual(filters, defaultFilters), [filters]);

  const listingTypeOptions = useMemo<SegmentedOption<ListingTypeValue>[]>(
    () => [
      // Rent first — it's the product default scope (and the first, rightmost, in RTL).
      { value: 'rent', label: t('search.filters.listingTypeRent') },
      { value: 'buy', label: t('search.filters.listingTypeBuy') },
      { value: 'all', label: t('search.filters.listingTypeAll') },
    ],
    [t],
  );

  const categoryOptions = useMemo<SegmentedOption<CategoryValue>[]>(
    () => [
      { value: '', label: t('search.filters.categoryAll') },
      { value: 'residential', label: t('search.filters.categoryResidential') },
      { value: 'commercial', label: t('search.filters.categoryCommercial') },
    ],
    [t],
  );

  const handleInputChange = useCallback(
    <K extends keyof FilterState>(field: K, value: FilterState[K]) => {
      onFiltersChange({ [field]: value } as Partial<FilterState>);
    },
    [onFiltersChange],
  );

  // The change rules live in ../filterActions, shared with the filter bar's pills
  // so a change behaves the same wherever it is made.
  const handleListingTypeChange = useCallback(
    (listingType: ListingTypeValue) => onFiltersChange(listingTypeChange(listingType)),
    [onFiltersChange],
  );

  const handleCategoryChange = useCallback(
    (category: CategoryValue) => onFiltersChange(categoryChange(filters, category)),
    [filters, onFiltersChange],
  );

  const handleTypeChange = useCallback(
    (propertyType: string, option: SearchTypeOption | null) =>
      onFiltersChange(typeChange(filters, propertyType, option)),
    [filters, onFiltersChange],
  );

  const handleRangeChange = useCallback(
    (key: SearchRangeKey, next: SearchRangeBounds) => onFiltersChange(rangeChange(key, next)),
    [onFiltersChange],
  );

  // The ranges' «اختر نوع العقار…» hint: bring the type tiles into view and focus the chosen one.
  const focusTypePicker = useCallback(() => {
    const list = typeof document !== 'undefined' ? document.getElementById(typePickerId) : null;
    if (!list) return;
    list.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    const tile =
      list.querySelector<HTMLElement>('[data-option-tile][tabindex="0"]') ??
      list.querySelector<HTMLElement>('[data-option-tile]');
    tile?.focus({ preventScroll: true });
  }, [typePickerId]);

  // Handler for landmark selection
  const handleLandmarkSelect = useCallback(
    (place: PlaceResult | null) => {
      if (place) {
        const landmark: LandmarkFilter = {
          placeId: place.placeId,
          name: place.name,
          lat: place.lat,
          lng: place.lng,
        };
        onFiltersChange({ landmark, radiusKm: filters.radiusKm || 5 });
      } else {
        onFiltersChange({ landmark: null, radiusKm: undefined });
      }
    },
    [onFiltersChange, filters.radiusKm],
  );

  const handleRadiusChange = useCallback(
    (radius: number) => {
      onFiltersChange({ radiusKm: radius });
    },
    [onFiltersChange],
  );

  const priceRangeLabel = useMemo(() => {
    const min = formatAmount(filters.minPrice);
    const max = formatAmount(filters.maxPrice);
    if (min && max) return `${currencyLabel} ${min} - ${max}`;
    if (min) return `${currencyLabel} ${min}+`;
    if (max) return `≤ ${currencyLabel} ${max}`;
    return '';
  }, [filters.maxPrice, filters.minPrice, currencyLabel]);

  const activeBadges: Array<{ key: string; label: string; onClear: () => void }> = [];

  if (filters.query) {
    activeBadges.push({
      key: 'query',
      label: `${t('search.filters.searchLabel')}: “${filters.query}”`,
      onClear: () => handleInputChange('query', ''),
    });
  }

  if (filters.propertyType) {
    activeBadges.push({
      key: 'propertyType',
      label: t(`search.filters.propertyTypeOptions.${filters.propertyType}`, { defaultValue: filters.propertyType }),
      onClear: () => handleInputChange('propertyType', ''),
    });
  }

  if (filters.category) {
    activeBadges.push({
      key: 'category',
      label:
        filters.category === 'residential'
          ? t('search.filters.categoryResidential')
          : t('search.filters.categoryCommercial'),
      onClear: () => handleInputChange('category', ''),
    });
  }

  if (filters.listingType && filters.listingType !== 'all') {
    activeBadges.push({
      key: 'listingType',
      label: filters.listingType === 'rent' ? t('search.filters.listingTypeRent') : t('search.filters.listingTypeBuy'),
      onClear: () => handleListingTypeChange('all'),
    });
  }

  if (priceRangeLabel) {
    activeBadges.push({
      key: 'price',
      label: priceRangeLabel,
      onClear: () => onFiltersChange({ minPrice: '', maxPrice: '' }),
    });
  }

  // Compact "min-max" / "min+" / "≤ max" label for a range badge (Western digits).
  const rangeLabel = (min: string, max: string, suffix = '') => {
    const lo = formatAmount(min);
    const hi = formatAmount(max);
    if (lo && hi) return `${lo}-${hi}${suffix}`;
    if (lo) return `${lo}+${suffix}`;
    return `≤ ${hi}${suffix}`;
  };

  if ((filters.bedrooms || filters.bedroomsMax) && searchFields.bedrooms) {
    activeBadges.push({
      key: 'bedrooms',
      label: `${bedroomsLabel}: ${rangeLabel(filters.bedrooms, filters.bedroomsMax)}`,
      onClear: () => onFiltersChange({ bedrooms: '', bedroomsMax: '' }),
    });
  }

  if ((filters.bathrooms || filters.bathroomsMax) && searchFields.bathrooms) {
    activeBadges.push({
      key: 'bathrooms',
      label: `${bathroomsLabel}: ${rangeLabel(filters.bathrooms, filters.bathroomsMax)}`,
      onClear: () => onFiltersChange({ bathrooms: '', bathroomsMax: '' }),
    });
  }

  if ((filters.minArea || filters.maxArea) && searchFields.area) {
    activeBadges.push({
      key: 'area',
      label: `${areaLabel}: ${rangeLabel(filters.minArea, filters.maxArea, ` ${areaUnit}`)}`,
      onClear: () => onFiltersChange({ minArea: '', maxArea: '' }),
    });
  }

  amenityKeys.forEach((key) => {
    if (filters[key]) {
      activeBadges.push({
        key,
        label: t(`search.filters.amenities.${key}`),
        onClear: () => handleInputChange(key, false),
      });
    }
  });

  if (filters.landmark) {
    const radiusLabel = filters.radiusKm ? ` (${filters.radiusKm} km)` : '';
    activeBadges.push({
      key: 'landmark',
      label: `${t('search.nearYou.near', { defaultValue: 'Near' })} ${filters.landmark.name}${radiusLabel}`,
      onClear: () => onFiltersChange({ landmark: null, radiusKm: undefined }),
    });
  }

  const typeLabel = t('search.filters.propertyTypeLabel');

  const sections = (
    <div className="space-y-5">
      {/* Results order: first, so it is found where the filters are. */}
      {sort && (
        <FilterSection title={sort.label} titleId={`${baseId}-sort-title`}>
          <SelectMenu
            id={`${baseId}-sort`}
            label={sort.label}
            hideLabel
            value={sort.value}
            onChange={sort.onChange}
            options={sort.options}
            leadingIcon={<i className="ri-sort-desc" />}
          />
        </FilterSection>
      )}

      {/* Rent vs Buy - Primary Filter */}
      <FilterSection title={t('search.filters.listingTypeLabel')}>
        <SegmentedControl<ListingTypeValue>
          value={filters.listingType}
          onChange={handleListingTypeChange}
          options={listingTypeOptions}
          ariaLabel={t('search.filters.listingTypeLabel')}
          size="sm"
          fullWidth
        />
      </FilterSection>

      <FilterSection title={t('search.filters.searchLabel')} icon="ri-search-line">
        <SearchBar initialQuery={filters.query} className="w-full" onSearch={(q) => onFiltersChange({ query: q })} />
      </FilterSection>

      {/* Residential / Commercial macro-category, then the type tiles it scopes. Both
          sit above the ranges, because they decide which ranges show. */}
      <FilterSection title={t('search.filters.categoryLabel')}>
        <SegmentedControl<CategoryValue>
          value={filters.category}
          onChange={handleCategoryChange}
          options={categoryOptions}
          ariaLabel={t('search.filters.categoryLabel')}
          size="sm"
          fullWidth
        />
      </FilterSection>

      <FilterSection title={typeLabel}>
        <PropertyTypePicker
          variant="inline"
          id={typePickerId}
          value={filters.propertyType}
          category={filters.category}
          onChange={handleTypeChange}
          label={typeLabel}
          hideLabel
          columns={2}
          tileLayout="row"
        />
      </FilterSection>

      {/* Bedrooms · bathrooms · price · area — only the ones the type has. */}
      <FilterSection>
        <RangeFilterGrid
          propertyType={filters.propertyType}
          category={filters.category}
          listingType={filters.listingType}
          value={ranges}
          onChange={handleRangeChange}
          layout="stacked"
          idPrefix={`${baseId}-range`}
          onRequestType={focusTypePicker}
          pricePeriod={filters.pricePeriod}
          // A typed amount belongs to its period: switching clears the price range.
          onPricePeriodChange={(pricePeriod) => onFiltersChange(pricePeriodChange(pricePeriod))}
        />
      </FilterSection>

      {amenityKeys.length > 0 && (
        <FilterSection
          title={t('property:property.sections.features')}
          titleId={`${amenitiesListId}-title`}
          action={
            <button
              type="button"
              onClick={() => setShowAmenities((previous) => !previous)}
              aria-expanded={showAmenities}
              aria-controls={amenitiesListId}
              className={[
                'inline-flex min-h-[2.75rem] items-center gap-1 rounded-lg px-2 text-[0.875rem] text-[var(--pv-color-primary)] transition-colors hover:text-[var(--pv-color-primary-dark)]',
                FOCUS_RING,
              ].join(' ')}
            >
              <span>{showAmenities ? t('search.filters.showLess') : t('search.filters.showMore')}</span>
              <i className={`ri-arrow-${showAmenities ? 'up' : 'down'}-s-line text-[1rem] leading-none`} aria-hidden="true" />
            </button>
          }
        >
          {/* The `hidden` attribute folds the outer box; the flex row sits inside it,
              because a `flex` utility on the same element would override [hidden]. */}
          <div id={amenitiesListId} hidden={!showAmenities}>
            <div role="group" aria-labelledby={`${amenitiesListId}-title`} className="flex flex-wrap gap-2">
              {amenityKeys.map((key) => {
                const active = filters[key];
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => handleInputChange(key, !active)}
                    className={[
                      'inline-flex min-h-[2.75rem] items-center gap-2 rounded-full border py-1.5 pe-3.5 ps-1.5 text-start text-[0.875rem] leading-snug transition-colors duration-150',
                      FOCUS_RING,
                      FOCUS_BORDER,
                      active
                        ? 'border-[var(--pv-color-primary)] bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)] shadow-[inset_0_0_0_1px_var(--pv-color-primary)]'
                        : 'border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] text-[var(--pv-color-text)] hover:border-[var(--pv-color-primary-light)] hover:bg-[var(--pv-color-surface-soft)]',
                    ].join(' ')}
                  >
                    {/* 1rem icon in a 2rem tile; the tile turns solid teal when chosen. */}
                    <span
                      className={[
                        'inline-grid h-8 w-8 shrink-0 place-items-center rounded-full transition-colors duration-150',
                        active
                          ? 'bg-[var(--pv-color-primary)] text-white dark:text-[var(--pv-color-surface)]'
                          : 'bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]',
                      ].join(' ')}
                      aria-hidden="true"
                    >
                      <DomainIcon id={AMENITY_ICON[key]} className="h-4 w-4" />
                    </span>
                    <span>{t(`search.filters.amenities.${key}`)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </FilterSection>
      )}

      {/* Near You - Landmark Search */}
      <FilterSection title={t('search.nearYou.title', { defaultValue: 'Near Landmark' })} icon="ri-map-pin-line">
        <p className="text-[0.875rem] text-[var(--pv-color-text-muted)]">
          {t('search.nearYou.description', { defaultValue: 'Find properties near schools, malls, hospitals, and more' })}
        </p>
        <LandmarkSearch
          onSelect={handleLandmarkSelect}
          selectedPlace={filters.landmark ? {
            placeId: filters.landmark.placeId,
            name: filters.landmark.name,
            address: '',
            lat: filters.landmark.lat,
            lng: filters.landmark.lng,
            types: [],
          } : null}
        />
        {filters.landmark && (
          <RadiusSelector
            value={filters.radiusKm || 5}
            onChange={handleRadiusChange}
            className="mt-3"
          />
        )}
      </FilterSection>

      {activeBadges.length > 0 && (
        <FilterSection title={t('search.filters.activeTitle')}>
          <ul className="flex flex-wrap gap-2">
            {activeBadges.map((badge) => (
              <li key={badge.key}>
                {/* The whole chip removes the filter; ::before stretches the hit area to 2.75rem. */}
                <button
                  type="button"
                  onClick={badge.onClear}
                  aria-label={t('search.filters.removeFilter', { label: badge.label })}
                  className={[
                    "relative inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-full border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] pe-2.5 ps-3.5 text-[0.875rem] text-[var(--pv-color-text)] transition-colors hover:border-[var(--pv-color-primary-light)] hover:text-[var(--pv-color-primary)] before:absolute before:inset-x-0 before:-inset-y-1 before:content-['']",
                    FOCUS_RING,
                    FOCUS_BORDER,
                  ].join(' ')}
                >
                  <span>{badge.label}</span>
                  <i className="ri-close-line text-[1rem] leading-none text-[var(--pv-color-text-muted)]" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </FilterSection>
      )}
    </div>
  );

  if (layout === 'sheet') return sections;

  return (
    <div className="space-y-6 rounded-3xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] px-5 py-6 text-[var(--pv-color-text-muted)] shadow-brand-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="font-display text-lg text-[var(--pv-color-text)]">
            {t('search.filters.title')}
            {resultCount > 0 ? (
              <span className="ms-2 text-[0.875rem] font-normal tabular-nums text-[var(--pv-color-text-muted)]">
                ({formatAmount(resultCount)})
              </span>
            ) : null}
          </h3>
          <p className="text-[0.875rem] text-[var(--pv-color-text-muted)]">{t('search.filters.subtitle')}</p>
        </div>
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onClearAll}
            className={[
              'inline-flex min-h-[2.75rem] shrink-0 items-center rounded-full border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] px-4 text-[0.875rem] text-[var(--pv-color-primary)] transition-colors hover:border-[var(--pv-color-primary-light)] hover:text-[var(--pv-color-primary-dark)]',
              FOCUS_RING,
              FOCUS_BORDER,
            ].join(' ')}
          >
            {t('search.filters.clear')}
          </button>
        )}
      </div>

      {sections}
    </div>
  );
}
